import { version as msalBrowserVersion } from "@azure/msal-browser-1p";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { MsalHelper } from "./MsalHelper";
import { isLoginRequiredAuthError } from "./MsalUtilities";
import { NestedAppAuthHelper } from "./NestedAppAuthHelper";
// DUAL MSAL: When adding a V2 MSAL version, add these imports:
// import { version as msalBrowserV2Version } from "@azure/msal-browser-1p-v2";
// import { MsalHelperV2 } from "./MsalHelperV2";
// import { isLoginRequiredAuthErrorV2 } from "./MsalUtilitiesV2";
// import { NestedAppAuthHelperV2 } from "./NestedAppAuthHelperV2";
// Then update the V2 branches below to use the V2 classes instead of the V1 classes.
// See: docs/ai/architecture/msal/dual-msal-upgrade.md
/**
 * Unified auth router that initializes either MSAL V1 or MSAL V2 helpers based on a localStorage version stamp.
 * Handles both the web (MsalHelper) and NAA (NestedAppAuthHelper) paths.
 *
 * Currently both V1 and V2 branches use the same MSAL version (single MSAL build).
 * When upgrading MSAL, copy V1 classes to create V2 versions, update the V2 branches
 * to use the new classes, and add "-v2" npm aliases.
 */ export class MsalAuthRouter {
    /**
     * Determines whether to use MSAL V2 based on the DisableMsalV2 ChangeGate and the localStorage stamp.
     * If the ChangeGate is ON, clears the stamp and returns false.
     * @param configProvider Config provider for reading the ChangeGate
     * @param loggers Logger context
     * @param label Label for trace logs (e.g., "MsalHelper" or "NestedAppAuth")
     * @returns true if V2 should be used
     */ static shouldUseV2(configProvider, loggers, label) {
        if (configProvider().changeGates.DisableMsalV2) {
            loggers.traceLogger.logTrace(0x1e0030e1 /* tag_4add7 */ , TraceLevel.Info, `[MsalAuthRouter:${label}] DisableMsalV2 ChangeGate is ON. Clearing stamp and forcing V1.`);
            MsalAuthRouter.clearMsalV2Stamp(loggers);
            return false;
        }
        const useV2 = MsalAuthRouter.isStampMsalV2(loggers);
        loggers.traceLogger.logTrace(0x1e0030e0 /* tag_4add6 */ , TraceLevel.Info, `[MsalAuthRouter:${label}] Version check [useV2=${useV2}]`);
        return useV2;
    }
    /**
     * Initialize the Web MsalHelper.
     * @param msalConfigSettings MSAL configuration
     * @param msalHelperOptions MSAL helper options
     * @param loggers Logger context
     * @param configProvider Config provider
     * @param isUnauthenticatedFlow True when userId or tenantId is missing (unauthenticated flow)
     */ static async initializeMsalHelper(msalConfigSettings, msalHelperOptions, loggers, configProvider, isUnauthenticatedFlow) {
        if (MsalAuthRouter._msalHelperInstance != null) {
            throw new Error("MsalAuthRouter msalHelperInstance is not null. Don't initialize more than once.");
        }
        // For unauthenticated flow: stamp V2 before the version check so this page load uses V2.
        // The flight value comes from the server-rendered config (available pre-auth).
        if (isUnauthenticatedFlow && configProvider().flights.EnableUnauthenticatedMsalV2) {
            loggers.traceLogger.logTrace(0x1e0030df /* tag_4add5 */ , TraceLevel.Info, `[MsalAuthRouter:MsalHelper] Unauthenticated flow with EnableUnauthenticatedMsalV2 flight ON. Writing stamp.`);
            MsalAuthRouter.writeMsalV2Stamp(loggers);
        }
        if (MsalAuthRouter.shouldUseV2(configProvider, loggers, "MsalHelper")) {
            // DUAL MSAL: Replace MsalHelper with MsalHelperV2 here when adding a new MSAL version.
            const helperV2 = await MsalHelper.initialize(msalConfigSettings, msalHelperOptions, loggers);
            MsalAuthRouter._msalHelperInstance = helperV2;
            loggers.traceLogger.logTrace(0x1e0030de /* tag_4add4 */ , TraceLevel.Info, `[MsalAuthRouter:MsalHelper] MSAL V2 initialized successfully`);
            return;
        }
        // Default: MSAL V1
        const helperV1 = await MsalHelper.initialize(msalConfigSettings, msalHelperOptions, loggers);
        MsalAuthRouter._msalHelperInstance = helperV1;
        loggers.traceLogger.logTrace(0x1e0030dc /* tag_4add2 */ , TraceLevel.Info, `[MsalAuthRouter:MsalHelper] MSAL V1 initialized`);
        // If it's authenticatedFlow and EnableAuthenticatedMsalV2 flight is ON, write the MSAL V2 stamp
        if (!isUnauthenticatedFlow && configProvider().flights.EnableAuthenticatedMsalV2) {
            MsalAuthRouter.writeMsalV2Stamp(loggers);
        }
    }
    /**
     * Initializes the redirect bridge on the blank redirect page (COOP-compatible silent auth).
     * Only calls through to MsalHelperV2 when the V2 stamp is present in localStorage.
     * Must be called before rendering the app — callers should return immediately after.
     * @param loggers Logger context
     */ static async initializeRedirectBridge(loggers) {
        if (MsalAuthRouter.isStampMsalV2(loggers)) {
            loggers.traceLogger.logTrace(0x1e0030da /* tag_4add0 */ , TraceLevel.Info, `[MsalAuthRouter] initializeRedirectBridge: V2 stamp found, delegating to MsalHelperV2.`);
            // DUAL MSAL: Replace MsalHelper with MsalHelperV2 here when adding a new MSAL version.
            await MsalHelper.initializeRedirectBridge(loggers);
        } else {
            loggers.traceLogger.logTrace(0x1d8db403 /* tag_391qd */ , TraceLevel.Info, `[MsalAuthRouter] initializeRedirectBridge: V2 stamp not found, delegating to MsalHelper.`);
            await MsalHelper.initializeRedirectBridge(loggers);
        }
    }
    /**
     * Get the MsalHelper instance or throw if it doesn't exist yet
     */ static get msalHelperInstance() {
        if (MsalAuthRouter._msalHelperInstance == null) {
            throw new Error("Cannot retrieve msalHelperInstance as MsalAuthRouter is uninitialized");
        }
        return MsalAuthRouter._msalHelperInstance;
    }
    /**
     * Indicates whether the given error is an InteractionRequiredAuthError with login_required or interaction_required error code
     * @param error Error to check
     * @returns True if the error is a login required auth error, false otherwise
     */ static isLoginRequiredAuthError(error) {
        if (MsalAuthRouter.isStampMsalV2()) {
            // DUAL MSAL: Replace with isLoginRequiredAuthErrorV2 when adding a new MSAL version.
            return isLoginRequiredAuthError(error);
        } else {
            return isLoginRequiredAuthError(error);
        }
    }
    /**
     * Create and initialize either NestedAppAuthHelper (V1) or NestedAppAuthHelperV2 (V2).
     * @param config NAA configuration
     * @param loggers Logger context
     * @param options MSAL helper options
     * @param configProvider Config provider
     */ static async createAndInitializeNestedAppAuthHelper(config, loggers, options, configProvider) {
        // Note: For NAA, we're always in "authenticated" flow and since there's no e2e login flow, we can immediately stamp and use V2
        if (configProvider().flights.EnableAuthenticatedMsalV2) {
            MsalAuthRouter.writeMsalV2Stamp(loggers);
        }
        if (MsalAuthRouter.shouldUseV2(configProvider, loggers, "NestedAppAuth")) {
            loggers.traceLogger.logTrace(0x1e0030d9 /* tag_4addz */ , TraceLevel.Info, `[MsalAuthRouter:NestedAppAuth] Initializing V2`);
            // DUAL MSAL: Replace NestedAppAuthHelper with NestedAppAuthHelperV2 here when adding a new MSAL version.
            const helper = new NestedAppAuthHelper(config, loggers, options);
            await helper.initialize(config);
            loggers.traceLogger.logTrace(0x1e0030d8 /* tag_4addy */ , TraceLevel.Info, `[MsalAuthRouter:NestedAppAuth] V2 initialized successfully`);
            return helper;
        }
        loggers.traceLogger.logTrace(0x1e0030d6 /* tag_4addw */ , TraceLevel.Info, `[MsalAuthRouter:NestedAppAuth] Initializing V1`);
        const helper = new NestedAppAuthHelper(config, loggers, options);
        await helper.initialize(config);
        return helper;
    }
    /**
     * Reads the stamp from localStorage and compares to the current target version.
     * @param loggers Optional logger context for tracing errors
     * @returns true if the stamp matches CURRENT_MSAL_V2_VERSION
     */ static isStampMsalV2(loggers) {
        try {
            return window.localStorage.getItem(MsalAuthRouter.MSAL_V2_VERSION_KEY) === MsalAuthRouter.CURRENT_MSAL_V2_VERSION;
        } catch  {
            loggers?.traceLogger.logTrace(0x1d8dd8d8 /* tag_3939y */ , TraceLevel.Error, `[MsalAuthRouter] Error reading MSAL V2 stamp from localStorage`);
            return false;
        }
    }
    /**
     * Writes the current MSAL V2 version to the localStorage stamp.
     * @param loggers Logger context for tracing the stamp write
     */ static writeMsalV2Stamp(loggers) {
        loggers.traceLogger.logTrace(0x1d8dd8d7 /* tag_3939x */ , TraceLevel.Info, `[MsalAuthRouter] Writing MSAL V2 stamp to localStorage`);
        try {
            window.localStorage.setItem(MsalAuthRouter.MSAL_V2_VERSION_KEY, MsalAuthRouter.CURRENT_MSAL_V2_VERSION);
        } catch  {
            loggers.traceLogger.logTrace(0x1d8dd8d6 /* tag_3939w */ , TraceLevel.Error, `[MsalAuthRouter] Error writing MSAL V2 stamp to localStorage`);
        }
    }
    /**
     * Clears the MSAL V2 stamp from localStorage.
     * @param loggers Logger context for tracing the stamp clear
     */ static clearMsalV2Stamp(loggers) {
        loggers.traceLogger.logTrace(0x1d8dd8d5 /* tag_3939v */ , TraceLevel.Info, `[MsalAuthRouter] Clearing MSAL V2 stamp from localStorage`);
        try {
            window.localStorage.removeItem(MsalAuthRouter.MSAL_V2_VERSION_KEY);
        } catch  {
            loggers.traceLogger.logTrace(0x1d8dd8d4 /* tag_3939u */ , TraceLevel.Error, `[MsalAuthRouter] Error clearing MSAL V2 stamp from localStorage`);
        }
    }
}
MsalAuthRouter._msalHelperInstance = null;
// ─── Stamp management (private) ───
/**
     * The current MSAL V2 version that this build targets.
     * Derived from the installed @azure/msal-browser-1p package.
     * The stamp stores this value; on read, it is compared against this constant.
     * When the MSAL dependency is bumped, existing stamps become stale and users
     * re-enroll via the flight.
     *
     * DUAL MSAL: When adding a V2, change this to import from @azure/msal-browser-1p-v2.
     */ MsalAuthRouter.CURRENT_MSAL_V2_VERSION = msalBrowserVersion;
MsalAuthRouter.MSAL_V2_VERSION_KEY = "planner-msal-v2-version";
